import React, { useEffect, useState } from 'react'
import { useAuth } from '../components/AuthContext'
import { checkAdminClient } from '../utils'
import BranchManager from '../components/BranchManager'
import { PublishProductManager } from '../components/PublishProductManager'
import CategoryManager from '../components/CategoryManager'
import { BrandManager } from '../components/BrandManager'
import { TestimonialManager } from '../components/TestimonialManager'
import MessagesManager from '../components/MessagesManager'
import { useNavigate } from 'react-router-dom'
import { IoStorefront, IoBusiness, IoGrid, IoLogoDropbox, IoChatbubbleEllipses, IoPeople } from 'react-icons/io5'

const Admin = () => {
  const { currentUser, isLoggedIn, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'products' | 'categories' | 'branches' | 'brands' | 'testimonials' | 'messages'>('products');
  const navigate = useNavigate();

  useEffect(() => {
    const verifyAdmin = async () => {
      // Wait for auth state to be determined
      if (authLoading) {
        return;
      }

      // Now we know for sure if user is logged in or not
      if (!isLoggedIn || !currentUser) {
        navigate('/login');
        return;
      }

      // Check admin privileges
      console.log("Checking admin status for user:", currentUser.email);
      const adminStatus = await checkAdminClient(currentUser);
      console.log("Admin status result:", adminStatus);
      setIsAdmin(adminStatus);
      setLoading(false);
    };

    verifyAdmin();
  }, [currentUser, isLoggedIn, authLoading, navigate]);

  if (loading || authLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-xl">Verifying admin access...</div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p>Please log in to access the admin panel.</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p>You do not have admin privileges.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-zinc-800">Admin Dashboard</h1>
          <p className="text-gray-600 mt-2">Manage your JKCC products, branches, and settings</p>
        </div>
        
        {/* Tab Navigation */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('products')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all ${
                activeTab === 'products'
                  ? 'text-zinc-800 border-b-2 border-yellow-500 bg-yellow-50'
                  : 'text-gray-600 hover:text-zinc-800 hover:bg-gray-50'
              }`}
            >
              <IoStorefront size={20} />
              Products
            </button>
            <button
              onClick={() => setActiveTab('categories')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all ${
                activeTab === 'categories'
                  ? 'text-zinc-800 border-b-2 border-yellow-500 bg-yellow-50'
                  : 'text-gray-600 hover:text-zinc-800 hover:bg-gray-50'
              }`}
            >
              <IoGrid size={20} />
              Categories
            </button>
            <button
              onClick={() => setActiveTab('branches')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all ${
                activeTab === 'branches'
                  ? 'text-zinc-800 border-b-2 border-yellow-500 bg-yellow-50'
                  : 'text-gray-600 hover:text-zinc-800 hover:bg-gray-50'
              }`}
            >
              <IoBusiness size={20} />
              Branches
            </button>
            <button
              onClick={() => setActiveTab('brands')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all ${
                activeTab === 'brands'
                  ? 'text-zinc-800 border-b-2 border-yellow-500 bg-yellow-50'
                  : 'text-gray-600 hover:text-zinc-800 hover:bg-gray-50'
              }`}
            >
              <IoLogoDropbox size={20} />
              Brands
            </button>
            <button
              onClick={() => setActiveTab('testimonials')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all ${
                activeTab === 'testimonials'
                  ? 'text-zinc-800 border-b-2 border-yellow-500 bg-yellow-50'
                  : 'text-gray-600 hover:text-zinc-800 hover:bg-gray-50'
              }`}
            >
              <IoPeople size={20} />
              Testimonials
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex items-center gap-2 px-6 py-4 font-semibold transition-all ${
                activeTab === 'messages'
                  ? 'text-zinc-800 border-b-2 border-yellow-500 bg-yellow-50'
                  : 'text-gray-600 hover:text-zinc-800 hover:bg-gray-50'
              }`}
            >
              <IoChatbubbleEllipses size={20} />
              Messages
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="transition-all duration-300">
          {activeTab === 'products' && <PublishProductManager />}
          {activeTab === 'categories' && <CategoryManager />}
          {activeTab === 'branches' && <BranchManager />}
          {activeTab === 'brands' && <BrandManager />}
          {activeTab === 'testimonials' && <TestimonialManager />}
          {activeTab === 'messages' && <MessagesManager />}
        </div>
      </div>
    </div>
  );
}

export default Admin